import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, GripVertical, Palette, ChevronDown, ChevronUp, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';
import { adminBenefitsAPI, adminStoresAPI } from '../../utils/api';
import { apiClient } from '../../services/api';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { API_BASE_URL } from '../../config';
import GradientColorPicker from './GradientColorPicker';
import { FlipCard } from '../ui/FlipCard';

function extractPct(title = '') {
  const m = String(title).match(/(\d{1,2})\s*%/);
  return m ? m[1] : null;
}

/* Resuelve una ruta de logo a URL absoluta. */
function urlDeLogo(ruta) {
  if (!ruta) return null;
  const r = String(ruta);
  return r.startsWith('http') ? r : `${API_BASE_URL}${r.startsWith('/') ? '' : '/'}${r}`;
}

/**
 * Traduce el estado del formulario a la forma que consume FlipCard.
 * Se usa en la vista previa en vivo: lo que ve el admin mientras escribe es el
 * componente real, no una maqueta (FLIPCARD_SPEC §8).
 */
function benefitDesdeFormulario(form, store) {
  return {
    storeName: store?.name || 'Tienda',
    headlineNumber: form.headlineNumber,
    headlineThin: form.headlineThin,
    headlineStrong: form.headlineStrong,
    scope: form.scope,
    allyLogo: urlDeLogo(form.allyLogo || store?.logoUrl || store?.logo),
    colorStart: form.colorStart,
    colorEnd: form.colorEnd,
    gradientAngle: form.gradientAngle,
    storeColor: store?.color,
    description: form.description,
    terms: form.terms,
  };
}

/* ── Vista previa: el componente real de la tarjeta, no una réplica ── */
function BenefitMemberPreview({ store, form }) {
  return (
    <div style={{ width: 220, margin: '0 auto' }}>
      <FlipCard benefit={benefitDesdeFormulario(form, store)} flippable={false} />
    </div>
  );
}

/* Longitud a partir de la cual un slot obliga a encoger tanto la letra que
   conviene avisar (FLIPCARD_SPEC §8). No es un límite: la tarjeta sigue
   funcionando, solo pierde impacto. */
const LARGO_COMODO = { headlineNumber: 6, headlineThin: 26, headlineStrong: 18, scope: 26 };

const SLOTS = [
  {
    campo: 'headlineNumber',
    etiqueta: 'Número',
    ayuda: 'Lo más grande de la tarjeta.',
    placeholder: '15%',
  },
  {
    campo: 'headlineThin',
    etiqueta: 'Texto fino',
    ayuda: 'Va pegado al número, en la misma línea.',
    placeholder: 'de descuento',
  },
  {
    campo: 'headlineStrong',
    etiqueta: 'Palabra fuerte',
    ayuda: 'Línea propia, siempre en mayúsculas.',
    placeholder: 'ADICIONAL',
  },
  {
    campo: 'scope',
    etiqueta: 'Alcance',
    ayuda: 'Chico, debajo del titular.',
    placeholder: 'en toda la tienda',
  },
];

/* Paleta sugerida de BRAND.md. El admin puede poner cualquier color, pero
   estos son los que el manual da por buenos. */
const PALETA_SUGERIDA = [
  ['Naranja', '#FA4D0A'],
  ['Coral', '#F73E48'],
  ['Rojo vivo', '#FF1F1F'],
  ['Rosa', '#F794B4'],
  ['Ámbar', '#FBA63C'],
  ['Lavanda', '#97B0F5'],
  ['Marino', '#2C3350'],
  ['Éxito', '#1A9C52'],
];

/**
 * Editor de los cinco slots de la flip card.
 *
 * Solo expone lo que el manual permite tocar: los textos, el logo del aliado y
 * el color. La proporción, el radio, los márgenes, las posiciones de las zonas,
 * los tamaños de fuente, la capa de gel y el logo vondi son constantes y no
 * aparecen aquí a propósito (FLIPCARD_SPEC §8).
 */
function FlipCardSlotsEditor({ form, onChange }) {
  const set = (campo, valor) => onChange({ ...form, [campo]: valor });

  const colorHeredado = !form.colorStart;

  return (
    <div className="space-y-4 rounded-2xl border border-gray-200 p-4">
      <div>
        <Label className="text-gray-900">Contenido de la tarjeta</Label>
        <p className="text-gray-400 text-xs mt-1">
          El formato de la tarjeta es fijo. Lo único que cambia es este texto y el color.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {SLOTS.map(({ campo, etiqueta, ayuda, placeholder }) => {
          const valor = form[campo] || '';
          const largo = valor.trim().length;
          const excedido = largo > LARGO_COMODO[campo];
          return (
            <div key={campo} className="space-y-1">
              <Label className="text-gray-900 text-sm">{etiqueta}</Label>
              <Input
                value={valor}
                onChange={(e) => set(campo, e.target.value)}
                className="bg-gray-50 border-gray-300 text-gray-900 rounded-2xl h-11"
                placeholder={placeholder}
              />
              <p className={`text-xs ${excedido ? 'text-amber-600' : 'text-gray-400'}`}>
                {excedido
                  ? 'Texto muy largo para este slot: la letra se va a ver bastante más chica.'
                  : ayuda}
              </p>
            </div>
          );
        })}
      </div>

      <div className="space-y-2">
        <Label className="text-gray-900 text-sm">Logo del aliado</Label>
        <Input
          value={form.allyLogo || ''}
          onChange={(e) => set('allyLogo', e.target.value)}
          className="bg-gray-50 border-gray-300 text-gray-900 rounded-2xl h-11"
          placeholder="Vacío = se usa el logo de la tienda"
        />
        <p className="text-gray-400 text-xs">
          Tiene que ser un PNG o SVG con fondo transparente y el logo en blanco: en la
          tarjeta va sobre color. Si el archivo trae fondo sólido, se verá el recuadro.
        </p>
      </div>

      <div className="space-y-2">
        <Label className="text-gray-900 text-sm">Color de la tarjeta</Label>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => onChange({ ...form, colorStart: '', colorEnd: '' })}
            className={`px-3 h-9 rounded-full text-xs font-medium border transition-all ${
              colorHeredado ? 'bg-black text-white border-black' : 'bg-white text-gray-700 border-gray-300'
            }`}
          >
            Heredar de la tienda
          </button>
          {PALETA_SUGERIDA.map(([nombre, hex]) => (
            <button
              key={hex}
              type="button"
              title={nombre}
              onClick={() => onChange({ ...form, colorStart: hex, colorEnd: '' })}
              className={`w-9 h-9 rounded-full border transition-all ${
                (form.colorStart || '').toUpperCase() === hex
                  ? 'border-black ring-2 ring-black/20'
                  : 'border-gray-300'
              }`}
              style={{ background: hex }}
            />
          ))}
          <label className="flex items-center gap-2 px-3 h-9 rounded-full text-xs font-medium border border-gray-300 cursor-pointer">
            <span
              className="inline-block w-3.5 h-3.5 rounded"
              style={{ background: form.colorStart || '#888', border: '1px solid rgba(0,0,0,0.15)' }}
            />
            Personalizado
            <input
              type="color"
              value={form.colorStart || '#FA4D0A'}
              onChange={(e) => onChange({ ...form, colorStart: e.target.value })}
              className="w-0 h-0 opacity-0 absolute pointer-events-none"
            />
          </label>
        </div>
        <p className="text-gray-400 text-xs">
          {colorHeredado
            ? 'La tarjeta usa el color de su tienda. Al elegir uno aquí, se separa de ella.'
            : 'El segundo tono del degradado se calcula solo; en las tarjetas rojas va hacia el rosa, como pide el manual.'}
        </p>
      </div>
    </div>
  );
}

/* ── Selector de color de texto (manual, con opción Automático) ── */
function TextColorPicker({ value, onChange }) {
  const presets = [
    { label: 'Blanco', val: '#FFFFFF' },
    { label: 'Negro',  val: '#0A0A0A' },
  ];
  const isAuto = !value || !value.trim();
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        type="button"
        onClick={() => onChange('')}
        className={`px-3 h-9 rounded-full text-xs font-medium border transition-all ${isAuto ? 'bg-black text-white border-black' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'}`}
      >
        Automático
      </button>
      {presets.map(p => {
        const active = !isAuto && value.toUpperCase() === p.val.toUpperCase();
        return (
          <button
            key={p.val}
            type="button"
            onClick={() => onChange(p.val)}
            className={`px-3 h-9 rounded-full text-xs font-medium border transition-all flex items-center gap-2 ${active ? 'border-black ring-2 ring-black/20' : 'border-gray-300 hover:border-gray-400'}`}
          >
            <span style={{ width: 14, height: 14, borderRadius: 4, background: p.val, border: '1px solid rgba(0,0,0,0.15)', display: 'inline-block' }} />
            {p.label}
          </button>
        );
      })}
      <label className="flex items-center gap-2 px-3 h-9 rounded-full text-xs font-medium border border-gray-300 hover:border-gray-400 cursor-pointer">
        <span style={{ width: 14, height: 14, borderRadius: 4, background: isAuto ? '#888' : value, border: '1px solid rgba(0,0,0,0.15)', display: 'inline-block' }} />
        Personalizado
        <input
          type="color"
          value={isAuto ? '#000000' : value}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: 0, height: 0, opacity: 0, position: 'absolute', pointerEvents: 'none' }}
        />
      </label>
    </div>
  );
}

/* ── Vista previa chica para elegir el color de una tienda ──
   También es la tarjeta real, solo que a menor tamaño: si fuera una réplica
   volvería a divergir, que es el problema que se acaba de resolver. */
function MiniDiscountCard({ storeName, color = '#1A1A1A', logo }) {
  return (
    <div style={{ width: 90, flexShrink: 0 }}>
      <FlipCard
        flippable={false}
        benefit={{
          storeName,
          headlineNumber: '20%',
          headlineThin: 'de descuento',
          headlineStrong: 'ADICIONAL',
          allyLogo: logo,
          storeColor: color,
        }}
      />
    </div>
  );
}

/* ── Store Card Appearance row ── */
function StoreAppearanceRow({ store, onSave }) {
  const [color, setColor] = useState(store.color || '#1A1A1A');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  /* El PDF va por su propia ruta: el editor guarda por JSON y un archivo
     necesita FormData. Se sube al momento, no al guardar la tarjeta. */
  const subirTerminosPdf = async (archivo) => {
    if (!archivo || !selectedBenefit) return;
    try {
      const datos = new FormData();
      datos.append('documento', archivo);
      const r = await apiClient.request(
        `${API_BASE_URL}/api/admin/benefits/${selectedBenefit.id}/terms-pdf`,
        { method: 'POST', body: datos }
      );
      setFormData((f) => ({ ...f, termsPdf: r?.data?.termsPdf || '' }));
      toast({ title: 'Documento cargado' });
      loadData();
    } catch (e) {
      toast({ title: 'No se pudo subir', description: e.message, variant: 'destructive' });
    }
  };

  const quitarTerminosPdf = async () => {
    if (!selectedBenefit) return;
    try {
      await apiClient.delete(`${API_BASE_URL}/api/admin/benefits/${selectedBenefit.id}/terms-pdf`);
      setFormData((f) => ({ ...f, termsPdf: '' }));
      toast({ title: 'Documento retirado' });
      loadData();
    } catch (e) {
      toast({ title: 'No se pudo quitar', description: e.message, variant: 'destructive' });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(store.id, color);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 16,
      background: '#fff', border: '1px solid rgba(0,0,0,0.08)',
      borderRadius: 16, padding: '14px 16px',
    }}>
      <MiniDiscountCard storeName={store.name} color={color} logo={store.logo} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: '#0A0A0A', marginBottom: 4 }}>{store.name}</div>
        <div style={{ fontSize: 11, color: '#6B6B70', marginBottom: 10 }}>Color de la tarjeta en el carrusel (degradado)</div>
        <GradientColorPicker value={color} onChange={setColor} />
        <div style={{ marginTop: 10 }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              height: 34, padding: '0 16px', borderRadius: 9,
              background: saved ? '#16A34A' : '#0A0A0A', color: '#fff',
              fontSize: 12, fontWeight: 600, border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s',
            }}
          >
            {saving ? 'Guardando…' : saved ? 'Guardado ✓' : 'Aplicar color'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Collapsible Card Appearance Panel ── */
function CardAppearancePanel({ stores, onSaveStoreColor }) {
  const [open, setOpen] = useState(false);
  const participatingStores = stores.filter(s => s.isParticipating);

  return (
    <div style={{ background: '#F5F5F7', borderRadius: 20, border: '1px solid rgba(0,0,0,0.08)', overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 12,
          padding: '16px 20px', background: 'none', border: 'none', cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: 10, background: '#0A0A0A',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Palette size={18} color="#fff" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#0A0A0A' }}>Apariencia de Tarjetas del Carrusel</div>
          <div style={{ fontSize: 12, color: '#6B6B70', marginTop: 1 }}>
            Edita el color de cada tienda tal como aparece en la landing page
          </div>
        </div>
        {open ? <ChevronUp size={18} color="#6B6B70" /> : <ChevronDown size={18} color="#6B6B70" />}
      </button>

      {open && (
        <div style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {participatingStores.length === 0 && (
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#6B6B70', fontSize: 13 }}>
              No hay tiendas participantes aún.
            </div>
          )}
          {participatingStores.map(store => (
            <StoreAppearanceRow
              key={store.id}
              store={{ ...store, logo: store.logoUrl || store.logo }}
              onSave={onSaveStoreColor}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Editor de líneas de texto de la flip card (palabra por palabra, tamaño y orden) ── */
function CardLinesEditor({ lines, onChange }) {
  const items = lines ?? [];
  const setLine  = (i, patch) => onChange(items.map((l, j) => j === i ? { ...l, ...patch } : l));
  const addLine  = () => onChange([...items, { text: '', size: 20 }]);
  const removeLine = (i) => onChange(items.filter((_, j) => j !== i));
  const moveLine = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div className="space-y-2">
      {items.map((line, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            value={line.text ?? ''}
            onChange={(e) => setLine(i, { text: e.target.value })}
            className="bg-gray-50 border-gray-300 text-gray-900 rounded-2xl h-11 flex-1"
            placeholder={`Línea ${i + 1}`}
          />
          <Input
            type="number"
            value={line.size ?? 20}
            onChange={(e) => setLine(i, { size: Number(e.target.value) })}
            className="bg-gray-50 border-gray-300 text-gray-900 rounded-2xl h-11 w-20"
            min={10}
            max={60}
            title="Tamaño de fuente"
          />
          <button type="button" onClick={() => moveLine(i, -1)} disabled={i === 0} className="p-2 text-gray-500 hover:text-gray-900 disabled:opacity-30">
            <ArrowUp className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => moveLine(i, 1)} disabled={i === items.length - 1} className="p-2 text-gray-500 hover:text-gray-900 disabled:opacity-30">
            <ArrowDown className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => removeLine(i)} className="p-2 text-red-500 hover:text-red-700">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addLine}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm font-medium transition-colors"
      >
        <Plus className="w-4 h-4" />
        Agregar línea
      </button>
      <p className="text-gray-400 text-xs">
        Si no agregas líneas, la tarjeta usa el texto automático ("{'{%}'} de Descuento ADICIONAL").
      </p>
    </div>
  );
}

const SortableBenefitItem = ({ benefit, onEdit, onDelete }) => {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: benefit.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white border border-gray-200 rounded-3xl p-6 hover:border-gray-300 transition-all ${isDragging ? 'bg-gray-50' : ''}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-700 mt-1"
          >
            <GripVertical className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h3 className="text-xl font-semibold text-gray-900">{benefit.storeName}</h3>
              {benefit.isNew && (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                  Nuevo
                </span>
              )}
              {benefit.isActive ? (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                  Activo
                </span>
              ) : (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-200">
                  Inactivo
                </span>
              )}
              {benefit.onePerUser ? (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                  Una vez por cliente
                </span>
              ) : (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                  Ilimitado
                </span>
              )}
            </div>
            <p className="text-gray-800 mb-1">{benefit.title}</p>
            <p className="text-gray-500 mb-2">{benefit.description}</p>
            <p className="text-gray-400 text-xs mt-2">Orden: {benefit.displayOrder ?? 0}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => onEdit(benefit)} variant="outline" className="border-gray-200 text-gray-700 bg-transparent hover:bg-gray-100 rounded-full">
            <Edit className="w-4 h-4" />
          </Button>
          <Button onClick={() => onDelete(benefit.id)} variant="outline" className="border-red-200 text-red-600 bg-transparent hover:bg-red-50 rounded-full">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const BenefitsManagement = () => {
  const [benefits, setBenefits] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedBenefit, setSelectedBenefit] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    storeId: '',
    title: '',
    description: '',
    isActive: true,
    onePerUser: false,
    accessType: 'premium',
    terms: '',
    termsPdf: '',
    discountPercentage: '',
    perkType: 'percentage',
    perkLabel: '',
    perkValue: '',
    cardLines: [],
    textColor: '',
    // Slots de la flip card (FLIPCARD_SPEC §3)
    headlineNumber: '',
    headlineThin: '',
    headlineStrong: '',
    scope: '',
    allyLogo: '',
    colorStart: '',
    colorEnd: '',
    gradientAngle: 100,
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [benefitsRes, storesRes] = await Promise.all([
        adminBenefitsAPI.getAll(),
        adminStoresAPI.getAll(),
      ]);
      const sortedBenefits = benefitsRes.data.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
      setBenefits(sortedBenefits);
      setStores(storesRes.data.filter(s => s.isParticipating));
    } catch (error) {
      toast({ title: "Error", description: "No se pudieron cargar los datos", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (benefit) => {
    setSelectedBenefit(benefit);
    setFormData({
      storeId: benefit.storeId,
      title: benefit.title,
      description: benefit.description,
      isActive: benefit.isActive !== undefined ? benefit.isActive : true,
      onePerUser: benefit.onePerUser !== undefined ? benefit.onePerUser : false,
      accessType: benefit.accessType || 'premium',
      terms: benefit.terms || '',
      termsPdf: benefit.termsPdf || '',
      discountPercentage: benefit.discountPercentage ?? '',
      perkType: benefit.perkType || 'percentage',
      perkLabel: benefit.perkLabel || '',
      perkValue: benefit.perkValue ?? '',
      cardLines: benefit.cardLines || [],
      textColor: benefit.textColor || '',
      headlineNumber: benefit.headlineNumber || '',
      headlineThin: benefit.headlineThin || '',
      headlineStrong: benefit.headlineStrong || '',
      scope: benefit.scope || '',
      allyLogo: benefit.allyLogo || '',
      // Vacío = hereda el color de la tienda. Se deja así de propósito para que
      // se vea cuándo una tarjeta se separó del color de su tienda.
      colorStart: benefit.colorStart || '',
      colorEnd: benefit.colorEnd || '',
      gradientAngle: benefit.gradientAngle ?? 100,
    });
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        discountPercentage: formData.discountPercentage === '' ? null : Number(formData.discountPercentage),
        perkValue: formData.perkValue === '' ? null : Number(formData.perkValue),
        cardLines: formData.cardLines.filter(l => (l.text ?? '').trim() !== ''),
        textColor: formData.textColor && formData.textColor.trim() ? formData.textColor : null,
        // Un slot vacío se guarda como null, no como cadena vacía: así el
        // componente lo trata como ausente y recentra el bloque.
        ...['headlineNumber', 'headlineThin', 'headlineStrong', 'scope', 'allyLogo', 'colorStart', 'colorEnd']
          .reduce((acc, k) => {
            const v = (formData[k] ?? '').toString().trim();
            acc[k] = v === '' ? null : v;
            return acc;
          }, {}),
        gradientAngle: Number(formData.gradientAngle) || 100,
      };
      if (selectedBenefit) {
        const response = await adminBenefitsAPI.update(selectedBenefit.id, payload);
        setBenefits(benefits.map(b => b.id === selectedBenefit.id ? response.data : b));
      } else {
        const response = await adminBenefitsAPI.create(payload);
        setBenefits([...benefits, response.data]);
      }
      setSelectedBenefit(null);
      setIsAdding(false);
      toast({ title: "Beneficio guardado", description: "El beneficio ha sido actualizado correctamente" });
      loadData();
    } catch (error) {
      toast({ title: "Error", description: error.message || "No se pudo guardar el beneficio", variant: "destructive" });
    }
  };

  const handleDelete = async (benefitId) => {
    try {
      await adminBenefitsAPI.delete(benefitId);
      setBenefits(benefits.filter(b => b.id !== benefitId));
      toast({ title: "Beneficio eliminado", description: "El beneficio ha sido eliminado" });
    } catch (error) {
      toast({ title: "Error", description: error.message || "No se pudo eliminar el beneficio", variant: "destructive" });
    }
  };

  const handleAdd = () => {
    setFormData({
      storeId: '', title: '', description: '', isActive: true, onePerUser: false,
      accessType: 'premium', terms: '', termsPdf: '',
      discountPercentage: '', perkType: 'percentage', perkLabel: '', perkValue: '', cardLines: [], textColor: '',
      headlineNumber: '', headlineThin: '', headlineStrong: '', scope: '',
      allyLogo: '', colorStart: '', colorEnd: '', gradientAngle: 100,
    });
    setIsAdding(true);
  };

  const handleSaveStoreColor = async (storeId, color) => {
    try {
      await adminStoresAPI.update(storeId, { color });
      setStores(prev => prev.map(s => s.id === storeId ? { ...s, color } : s));
      toast({ title: 'Color actualizado', description: 'El color de la tarjeta se actualizó correctamente.' });
    } catch (err) {
      toast({ title: 'Error', description: err.message || 'No se pudo actualizar el color', variant: 'destructive' });
      throw err;
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = benefits.findIndex(b => b.id === active.id);
    const newIndex = benefits.findIndex(b => b.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reorderedBenefits = arrayMove(benefits, oldIndex, newIndex);
    const updatedBenefits = reorderedBenefits.map((benefit, index) => ({ ...benefit, displayOrder: index + 1 }));
    setBenefits(updatedBenefits);
    try {
      const updatePromises = reorderedBenefits.map((benefit, index) =>
        adminBenefitsAPI.update(benefit.id, { displayOrder: index + 1 })
      );
      await Promise.all(updatePromises);
      toast({ title: "Éxito", description: "Orden de beneficios actualizado exitosamente" });
    } catch (error) {
      toast({ title: "Error", description: error.message || "Error al actualizar orden", variant: "destructive" });
      loadData();
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <p className="text-gray-500">Cargando beneficios...</p>
      </div>
    );
  }

  return (
    <>
      <div className="p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Gestión de beneficios</h2>
              <p className="text-gray-500">Crea y administra beneficios para las tiendas. Arrastra y suelta para reordenar.</p>
            </div>
            <Button
              onClick={handleAdd}
              className="bg-black text-white hover:bg-black/90 rounded-full px-6 py-3 transition-all hover:scale-105 active:scale-95"
            >
              <Plus className="w-4 h-4 mr-2" />
              Crear beneficio
            </Button>
          </div>

          <CardAppearancePanel stores={stores} onSaveStoreColor={handleSaveStoreColor} />

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={benefits.map(b => b.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-3">
                {benefits.map((benefit) => (
                  <SortableBenefitItem
                    key={benefit.id}
                    benefit={benefit}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {benefits.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No hay beneficios disponibles. Crea uno nuevo para comenzar.
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!selectedBenefit || isAdding} onOpenChange={() => { setSelectedBenefit(null); setIsAdding(false); }}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              {selectedBenefit ? 'Editar beneficio' : 'Crear beneficio'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Vista previa: como se ve la tarjeta dentro de Miembros */}
            <div className="space-y-2">
              <Label className="text-gray-900">Vista previa (como se ve en Miembros)</Label>
              <div className="bg-gray-100 rounded-2xl py-5 flex justify-center">
                <BenefitMemberPreview
                  store={stores.find(s => String(s.id) === String(formData.storeId))}
                  form={formData}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-900">Tienda</Label>
              <Select value={formData.storeId} onValueChange={(value) => setFormData({ ...formData, storeId: value })}>
                <SelectTrigger className="bg-gray-50 border-gray-300 text-gray-900 rounded-2xl h-12">
                  <SelectValue placeholder="Selecciona una tienda" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  {stores.map((store) => (
                    <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-900">Título corto</Label>
              <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="bg-gray-50 border-gray-300 text-gray-900 rounded-2xl h-12" placeholder="Ej: Solo este fin" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-900">Descripción del beneficio</Label>
              <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="bg-gray-50 border-gray-300 text-gray-900 rounded-2xl h-12" placeholder="Ej: Hasta 30% adicional hoy" />
            </div>
            {/* ── Candado: gratis de muestra vs premium (Módulo 1) ── */}
            <div className="space-y-2">
              <Label className="text-gray-900">Acceso</Label>
              <Select
                value={formData.accessType}
                onValueChange={(value) => setFormData({ ...formData, accessType: value })}
              >
                <SelectTrigger className="bg-gray-50 border-gray-300 text-gray-900 rounded-2xl h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="premium">Premium — requiere membresía</SelectItem>
                  <SelectItem value="free_sample">Gratis de muestra — canjeable sin pagar</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-gray-400 text-xs">
                Los beneficios premium se mostrarán con candado a quienes tengan cuenta
                gratuita. Los de muestra son los que puede canjear alguien que aún no paga.
              </p>
            </div>

            {/* ── T&C propios de esta promoción (Módulo 1 / Módulo 2) ── */}
            <div className="space-y-2">
              <Label className="text-gray-900">Términos y condiciones de la promoción</Label>
              <Textarea
                value={formData.terms}
                onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
                className="bg-gray-50 border-gray-300 text-gray-900 rounded-2xl min-h-[96px]"
                placeholder={'Ej: Válido solo sábados y domingos.\nNo acumulable con otras promociones.\nAplica en productos seleccionados.'}
                maxLength={5000}
              />
              <p className="text-gray-400 text-xs">
                Opcional. Es el resumen que se lee dentro de la tarjeta. Si lo dejas vacío
                se usa el texto genérico de siempre.
              </p>
            </div>

            {/* ── El documento completo (Iván, 13-ago-2026) ── */}
            {selectedBenefit && (
              <div className="space-y-2">
                <Label className="text-gray-900">Documento de términos (PDF)</Label>
                <div className="flex items-center gap-3 flex-wrap">
                  {formData.termsPdf ? (
                    <>
                      <a
                        href={formData.termsPdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-vd-naranja underline underline-offset-2"
                      >
                        Ver el PDF cargado
                      </a>
                      <button
                        type="button"
                        onClick={quitarTerminosPdf}
                        className="text-xs text-gray-500 underline"
                      >
                        quitar
                      </button>
                    </>
                  ) : (
                    <span className="text-sm text-gray-400">Sin documento</span>
                  )}
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => subirTerminosPdf(e.target.files?.[0])}
                    className="text-xs"
                  />
                </div>
                <p className="text-gray-400 text-xs">
                  Máximo 8 MB. Se abre aparte desde la tarjeta, con el botón «Ver documento completo».
                </p>
              </div>
            )}

            <div className="space-y-4 pt-2 border-t border-gray-200">
              <div className="space-y-2">
                <Label className="text-gray-900">Tipo de beneficio</Label>
                <Select value={formData.perkType} onValueChange={(value) => setFormData({ ...formData, perkType: value })}>
                  <SelectTrigger className="bg-gray-50 border-gray-300 text-gray-900 rounded-2xl h-12">
                    <SelectValue placeholder="Selecciona el tipo" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="percentage">% de descuento adicional</SelectItem>
                    <SelectItem value="gift">Producto de regalo</SelectItem>
                    <SelectItem value="financing">Meses sin intereses</SelectItem>
                    <SelectItem value="other">Otro beneficio</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-gray-400 text-xs">
                  Este es el % o beneficio EXCLUSIVO de VONDI (no el descuento general de la tienda).
                </p>
              </div>

              {formData.perkType === 'percentage' ? (
                <div className="space-y-2">
                  <Label className="text-gray-900">% adicional por ser miembro VONDI</Label>
                  <Input
                    type="number" min={0} max={100}
                    value={formData.discountPercentage}
                    onChange={(e) => setFormData({ ...formData, discountPercentage: e.target.value })}
                    className="bg-gray-50 border-gray-300 text-gray-900 rounded-2xl h-12"
                    placeholder="Ej: 10"
                  />
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label className="text-gray-900">Etiqueta del beneficio</Label>
                    <Input
                      value={formData.perkLabel}
                      onChange={(e) => setFormData({ ...formData, perkLabel: e.target.value })}
                      className="bg-gray-50 border-gray-300 text-gray-900 rounded-2xl h-12"
                      placeholder="Ej: Producto de regalo en tu compra"
                    />
                  </div>

                  {/*
                    Lo que vale el beneficio en pesos.

                    Desde que la caja dejó de pedirle el monto al cajero, el
                    ahorro sale de aquí. Un regalo tiene un valor real que se da
                    de alta una vez; unos meses sin intereses no son un ahorro en
                    pesos y se dejan vacíos, y esos canjes salen en el historial
                    del cliente por su nombre en vez de con una cifra.
                  */}
                  <div className="space-y-2">
                    <Label className="text-gray-900">Valor en pesos (opcional)</Label>
                    <Input
                      type="number" min={0} step="0.01"
                      value={formData.perkValue}
                      onChange={(e) => setFormData({ ...formData, perkValue: e.target.value })}
                      className="bg-gray-50 border-gray-300 text-gray-900 rounded-2xl h-12"
                      placeholder="Ej: 350"
                    />
                    <p className="text-gray-400 text-xs">
                      Es lo que se le registra como ahorro al cliente cuando la tienda aplica este beneficio.
                      Déjalo vacío si no es un ahorro en pesos (meses sin intereses): el canje aparecerá en su
                      historial por el nombre del beneficio, sin monto.
                    </p>
                  </div>
                </>
              )}

              <FlipCardSlotsEditor form={formData} onChange={setFormData} />

              <div className="space-y-2">
                <Label className="text-gray-900">Texto de la flip card (formato anterior)</Label>
                <CardLinesEditor
                  lines={formData.cardLines}
                  onChange={(cardLines) => setFormData({ ...formData, cardLines })}
                />
                <p className="text-gray-400 text-xs">
                  Este campo quedó reemplazado por los cuatro slots de arriba y ya no se
                  dibuja en la tarjeta. Se conserva por ahora para poder consultar el texto
                  original con el que se rellenaron los slots.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-900">Color del texto</Label>
                <TextColorPicker
                  value={formData.textColor}
                  onChange={(textColor) => setFormData({ ...formData, textColor })}
                />
                <p className="text-gray-400 text-xs">
                  "Automático" elige blanco o negro según el fondo. Elige un color fijo si lo prefieres.
                </p>
              </div>
            </div>

            <div className="space-y-4 pt-2 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-gray-900">Estado</Label>
                  <p className="text-gray-500 text-sm">Activo / Inactivo</p>
                </div>
                <Switch checked={formData.isActive} onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })} />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-900">Tipo de uso</Label>
                <Select value={formData.onePerUser ? 'once' : 'unlimited'} onValueChange={(value) => setFormData({ ...formData, onePerUser: value === 'once' })}>
                  <SelectTrigger className="bg-gray-50 border-gray-300 text-gray-900 rounded-2xl h-12">
                    <SelectValue placeholder="Selecciona el tipo de uso" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="unlimited">Ilimitado</SelectItem>
                    <SelectItem value="once">Una vez por cliente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button onClick={handleSave} className="w-full bg-black text-white hover:bg-black/90 rounded-full py-6 transition-all hover:scale-105 active:scale-95">
              {selectedBenefit ? 'Guardar cambios' : 'Crear beneficio'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Toaster />
    </>
  );
};

export default BenefitsManagement;
